import { useState } from 'react';
import { api, ApiError } from '../lib/api';

/**
 * The card for deleting a company or a project that was created by mistake.
 *
 * Shared by both so the two cannot drift into warning people differently about the same act.
 * The API is the thing that actually enforces any of this — it refuses unless the record is
 * empty, requires the reason, and writes it to the audit trail — so nothing here is a
 * security control. What it is for is making an irreversible action feel like one:
 *
 *   THE CODE HAS TO BE TYPED. A reason field alone can be filled in by someone who has not
 *   registered what they are about to do; typing `GOLD` in a box is a moment where the wrong
 *   record announces itself.
 *
 *   THE REFUSAL IS SHOWN AS WRITTEN. When the API says what is in the way — "4 work items,
 *   2 logged time entries" — that sentence is the useful part, so it is printed rather than
 *   replaced with a generic failure.
 */

/**
 * The two inputs, and the rule about when they are satisfied.
 *
 * Exported so the People page's modal and the card below share one definition of "ready to
 * delete". Two copies of that rule is how one of them ends up accepting a five-character
 * reason six months from now.
 */
export function useDeletionFields(code: string) {
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState('');

  return {
    reason,
    setReason,
    confirm,
    setConfirm,
    ready: reason.trim().length >= 10 && confirm.trim().toUpperCase() === code.toUpperCase(),
    reset: () => {
      setReason('');
      setConfirm('');
    },
  };
}

export function DeletionFields({
  what,
  code,
  fields,
  confirmHint,
}: {
  what: string;
  code: string;
  fields: ReturnType<typeof useDeletionFields>;
  confirmHint?: string;
}) {
  return (
    <>
      <label className="field">
        <span className="field-label">Why is it being deleted?</span>
        <textarea
          value={fields.reason}
          onChange={(e) => fields.setReason(e.target.value)}
          rows={2}
          placeholder="Created against the wrong customer during onboarding"
          autoFocus
        />
        <span className="field-hint">
          At least 10 characters. This is the only record left once the {what} is gone.
        </span>
      </label>

      <label className="field">
        <span className="field-label">
          Type <span className="mono">{code}</span> to confirm
        </span>
        <input
          value={fields.confirm}
          onChange={(e) => fields.setConfirm(e.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
        {confirmHint && <span className="field-hint">{confirmHint}</span>}
      </label>
    </>
  );
}

export function DeleteWithReason({
  what,
  name,
  code,
  path,
  onDeleted,
}: {
  /** "company" or "project", used in the prose so the card reads naturally in both places. */
  what: string;
  name: string;
  /** Typed by the user to confirm. Short and visible on the page they are already looking at. */
  code: string;
  /** The API path to DELETE, e.g. `/companies/<id>`. */
  path: string;
  onDeleted: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fields = useDeletionFields(code);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.del(path, { reason: fields.reason.trim() });
      onDeleted();
    } catch (err) {
      // A 409 here is the emptiness refusal, and its message names what is in the way.
      setError(err instanceof ApiError ? err.message : 'Could not delete it');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2>Delete this {what}</h2>
      </div>
      <div className="card-pad">
        <p className="muted" style={{ marginTop: 0 }}>
          For a {what} created by mistake. It only works while this one is still empty — once
          there is work, time or documents against it, deleting is refused and the way to take
          it out of use is to set its status to archived instead. This cannot be undone, and the
          reason you give is kept in the audit trail afterwards.
        </p>

        {error && <div className="callout">{error}</div>}

        {!open ? (
          <button type="button" className="btn btn-danger" onClick={() => setOpen(true)}>
            Delete {name}…
          </button>
        ) : (
          <form onSubmit={submit}>
            <DeletionFields what={what} code={code} fields={fields} />

            <div className="row">
              <button type="submit" className="btn btn-danger" disabled={busy || !fields.ready}>
                {busy ? 'Deleting…' : `Delete ${what}`}
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setOpen(false);
                  fields.reset();
                  setError(null);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
